import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Big } from 'big.js';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AppState } from 'src/app/app.states';
import { InvoicesActions } from 'src/app/features/invoices/state/invoices.action-types';
import { AcceptConditionsConfirmationDialogComponent } from '../accept-conditions-confirmation-dialog/accept-conditions-confirmation-dialog.component';
import { InvoiceService } from '../../../services/invoice.service';
import { InvoiceSelectors } from '../../../state/invoices.selector-types';

@Component({
  selector: 'app-accept-conditions-bulk-dialog',
  templateUrl: './accept-conditions-bulk-dialog.component.html',
  styleUrls: ['./accept-conditions-bulk-dialog.component.scss'],
})
export class AcceptConditionsBulkDialogComponent implements OnInit, OnDestroy {
  ngUnsubscribe: Subject<void> = new Subject<void>();

  view: 'list' | 'result' = 'list';
  bulks = [];
  acceptSucceeded = false;

  private allInvoices = [];
  private acceptedIds: string[] = [];

  resultConfig = {
    onSuccess: {
      title: 'Condiciones aceptadas',
      messsage: 'Ya estamos gestionando tus operaciones. Te avisamos apenas haya novedades.',
      showIcon: true,
      iconName: 'big-check-mark-icon',
    },
  };

  constructor(
    private store: Store<AppState>,
    private service: InvoiceService,
    public dialogRef: MatDialogRef<AcceptConditionsBulkDialogComponent>,
    private dialog: MatDialog,
    private spinner: NgxSpinnerService
  ) {}

  ngOnInit() {
    // The operations list only opens this dialog once its invoices are loaded, so a single read is
    // enough — re-reading on every store emission would wipe the checkbox selection whenever an
    // unrelated `update_client` socket event lands.
    this.store
      .select(InvoiceSelectors.all)
      .pipe(take(1))
      .subscribe((invoices: any) => {
        this.allInvoices = invoices || [];
        this.buildBulks();

        if (!this.bulks.length) {
          this.dialogRef.close();
        }
      });
  }

  ngOnDestroy() {
    this.spinner.hide();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  private buildBulks() {
    const remaining = this.allInvoices.filter(invoice => !this.acceptedIds.includes(invoice._id));

    this.bulks = this.service.getPendingConditionBulks(remaining).map((bulk, index) => ({
      ...bulk,
      expanded: index === 0,
      operations: bulk.operations.map(operation => ({ ...operation, selected: true })),
    }));
  }

  toggleDetail(bulk) {
    bulk.expanded = !bulk.expanded;
  }

  toggleOperation(bulk, operation) {
    operation.selected = !operation.selected;
  }

  selectedOperations(bulk) {
    return bulk.operations.filter(operation => operation.selected);
  }

  selectedCount(bulk): number {
    return this.selectedOperations(bulk).length;
  }

  totalToReceive(bulk): string {
    Big.DP = 40;
    Big.RM = 2;

    return this.selectedOperations(bulk)
      .reduce((total, operation) => total.plus(this.service.amountToReceive(operation) || 0), new Big(0))
      .toFixed(2);
  }

  acceptButtonLabel(bulk): string {
    const count = this.selectedCount(bulk);

    return count === 1 ? 'Aceptar 1 operación' : `Aceptar las ${count} operaciones`;
  }

  companyName(operation): string {
    return operation?.isConfirming ? operation?.supplier?.companyName : operation?.debtor?.companyName;
  }

  getCodes(operation): string {
    return (operation?.physicalInvoices || []).map(invoice => invoice.code).join(', ');
  }

  getDuration(operation) {
    return operation?.actualDuration || operation?.minimumDuration;
  }

  amountToReceive(operation): string {
    return this.service.amountToReceive(operation);
  }

  acceptBulk(bulk) {
    const selected = this.selectedOperations(bulk);

    if (!selected.length) {
      return;
    }

    this.dialog
      .open(AcceptConditionsConfirmationDialogComponent, {
        maxWidth: '416px',
        maxHeight: '100vh',
        height: 'auto',
        autoFocus: false,
        disableClose: true,
        data: {},
      })
      .afterClosed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.sendAcceptance(selected);
        }
      });
  }

  private sendAcceptance(operations) {
    const ids = operations.map(operation => operation._id);

    this.spinner.show();

    this.service
      .acceptConditionsInBulk(ids)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        error: () => this.showResult(false),
        complete: () => {
          this.acceptedIds = this.acceptedIds.concat(ids);
          this.showResult(true);
        },
      });
  }

  private showResult(succeeded: boolean) {
    this.acceptSucceeded = succeeded;
    this.view = 'result';
    this.spinner.hide();
  }

  onResultDone() {
    // Refresh the rest of the client portal (the operations list and its banner read the same store).
    this.store.dispatch(InvoicesActions.LoadAll());

    // On a mid-batch failure we don't know which of the PATCHes went through, so the honest move is to
    // close and let the list render whatever the reload returns.
    if (!this.acceptSucceeded) {
      this.dialogRef.close();
      return;
    }

    this.buildBulks();

    if (this.bulks.length) {
      this.view = 'list';
    } else {
      this.dialogRef.close();
    }
  }

  close() {
    this.dialogRef.close();
  }
}
